/*
 *   Copyright (c) 2022 nVipani Technology Solutions Private Limited.
 *   All rights reserved.
 */
import { Router } from "@angular/router";
import { UserType } from "../enums/userType.enums";
import { User } from "../models/user.interface";
import { types } from "mediasoup-client";
import { MediaType } from "../enums/mediaType.enums";
import hark from "hark";
import { DeviceDetectorService } from "ngx-device-detector";
import { createOrUpdateStreamAndAddTrack } from "./dom";

const getQueryParam = (param: string, router: Router): string => {
  if (!param) return null;
  return router["currentUrlTree"]["queryParams"][param];
};

const getAllQueryParams = (router: Router) => {
  if (!router) return {};

  return Object.keys(router["currentUrlTree"]["queryParams"]).reduce(
    (acc, each) => {
      acc[each] = getQueryParam(each, router);
      return acc;
    },
    {}
  );
};

const createUser = (
  name: string,
  type: UserType,
  id: string,
  title?: string,
  picURL?: string,
  joinedDevice?: string
): User => {
  return {
    name,
    type,
    id,
    title,
    joinedDevice,
    transport: null,
    producerTransport: null,
    consumerTransport: null,
    profilePicURL: picURL,
    transports: {
      video: {
        mediaType: null,
        stream: null,
      },
      audio: {
        mediaType: null,
        stream: null,
        volume: -Infinity,
      },
      screen: {
        mediaType: null,
        stream: null,
      },
    },
  };
};

const checkForExistingUser = (user: User, users: User[], socket): boolean => {
  return !users.find((each) => each.id === user.id) && user.id !== socket.id;
};

const addTransportToUser = (
  users: User[],
  id: string,
  transport: types.Transport
) => {
  const user = users.find((user) => user.id === id);
  if (!user) return null;
  user.transport = transport;
};

const addMediaTypeToUser = (
  users: User[],
  id: string,
  transportType: MediaType,
  mediaType: types.Consumer | types.Producer,
  paused?: boolean
) => {
  const user = users.find((user) => user.id === id);
  if (!user) return null;
  user.transports[transportType].mediaType = mediaType;
  console.log("IS THIS PAUSED", paused, id, users);
  if (paused) user.transports[transportType].mediaType.pause();
};

const addStreamToUser = (
  users: User[],
  id: string,
  stream: MediaStream,
  kind: MediaType
) => {
  const user = users.find((user) => user.id === id);
  if (!user) return null;
  user.transports[kind].stream = stream;
  if (kind === MediaType.AUDIO) {
    const audioVolume = hark(stream);
    audioVolume.on("volume_change", (volume) => {
      user.transports.audio.volume = volume;
      // console.log('User: ', user.name, ' Vol: ', volume);
    });
  }
};

const removeUser = (users: User[], id: string) =>
  users.filter((user) => user.id !== id);

const findUser = (users: User[], id: string) =>
  users.find((user) => user.id === id);

const getCurrentTime = () =>
  new Date().getHours() +
  ":" +
  (new Date().getMinutes() < 10 ? "0" : "") +
  new Date().getMinutes();

const setSelfUserProperties = (
  obj: { stream: MediaStream; mediaType: types.Producer | types.Consumer },
  user: User,
  type: MediaType
) => {
  user.transports[type] = { ...obj, volume: -Infinity };
};

const setUserType = (appointment, isExternal: boolean): UserType => {
  if (!isExternal) {
    const value = appointment.value;
    if (
      appointment.user &&
      !appointment.user.isSpot &&
      value.includes("External")
    ) {
      const type = appointment.user.type;
      if (type === "Customer") return UserType.CUSTOMER;
      else if (type === "Designer") return UserType.EXTERNAL_DESIGNER;
      else if (type === "SalesPerson") return UserType.EXTERNAL_SALES_PERSON;
      else if (type === "Other") return UserType.EXTERNAL_OTHER;
      else return type;
    }
    if (value === "isSalesPerson") {
      if (appointment.user.companies[0].userGroupType.name === "Admin") {
        return UserType.ADMIN;
      } else if (
        appointment.user.companies[0].userGroupType.name === "Manager"
      ) {
        return UserType.MANAGER;
      }
      return UserType.SALES_PERSON;
    } else if (value === "ExternalCustomer") return UserType.CUSTOMER;
    else if (value === "Customer") return UserType.CUSTOMER;
    else if (value === "InCharge") return UserType.IN_CHARGE;
  } else {
    const value = appointment.user.type;
    if (value === "Customer") return UserType.CUSTOMER;
    else if (value === "Designer") return UserType.EXTERNAL_DESIGNER;
    else if (value === "SalesPerson") return UserType.EXTERNAL_SALES_PERSON;
    else if (value === "Other") return UserType.EXTERNAL_OTHER;
    else return value;
  }
  return UserType.CUSTOMER;
};
const getProducingUser = (users) => {
  return users.find((user) => user.transports.video.stream) || null;
};
const verifyCustomer = (user: User) => {
  // const result = user && user.type !== UserType.CUSTOMER && !verifyGuest(user);
  const result =
    user &&
    (user.type === UserType.SALES_PERSON ||
      user.type === UserType.ADMIN ||
      user.type === UserType.MANAGER) &&
    !verifyGuest(user);
  return result;
};

const verifyGuest = (user: User) => {
  const result =
    user &&
    (user.type === UserType.CUSTOMER ||
      user.type === UserType.EXTERNAL_DESIGNER ||
      user.type === UserType.EXTERNAL_SALES_PERSON);
  return result;
};
const isSalesPerson = (user: User) => {
  const result = user && user.type === UserType.SALES_PERSON;
  return result;
};
const isShopKeeper = (user: User) => {
  const result =
    user &&
    (user.type === UserType.ADMIN ||
      user.type === UserType.MANAGER ||
      user.type === UserType.SALES_PERSON);
  return result;
};
const setActualDimensions = () => {
  const windowHeight = window.innerHeight * 0.01;
  const windowWidth = window.innerWidth * 0.01;
  document.documentElement.style.setProperty("--vh", `${windowHeight}px`);
  document.documentElement.style.setProperty("--vw", `${windowWidth}px`);
  return { windowHeight, windowWidth };
};
const throttle = (callBack, limit) => {
  let flag = true;
  return () => {
    if (flag) {
      callBack();
      flag = false;
      setTimeout(() => (flag = true), limit);
    }
  };
};
const getCompanyName = () => {
  var companyName;
  if (
    window.location.href.includes("srsm") ||
    window.location.href.includes("srshopping")
  ) {
    companyName = " - SR Shopping Mall";
  } else if (window.location.href.includes("jdx")) {
    companyName = " - JD Marketplace";
  } else {
    companyName = " - Whiteshop";
  }
  return companyName;
};

const applyDrag = (arr, dragResult) => {
  const { removedIndex, addedIndex, payload } = dragResult;
  if (removedIndex === null && addedIndex === null) return arr;

  const result = [...arr];
  let itemToAdd = payload;

  if (removedIndex !== null) {
    itemToAdd = result.splice(removedIndex, 1)[0];
  }

  if (addedIndex !== null) {
    result.splice(addedIndex, 0, itemToAdd);
  }

  return result;
};

const generateItems = (count, creator) => {
  const result = [];
  for (let i = 0; i < count; i++) {
    result.push(creator(i));
  }
  return result;
};

const validateBrowsers = (
  device: DeviceDetectorService,
  browsers: Array<Browser>
) => {
  const browser_name = device.browser.toLowerCase();
  const browser_version = Number(device.browser_version.split(".")[0]);

  for (let browser of browsers) {
    if (browser_name.includes(browser.name)) {
      return {
        supported: browser_version >= browser.lowest_version,
        browser,
      };
    }
  }

  // This browser is not using any of the above mentioned browsers
  return {
    supported: false,
  };
};

interface Browser {
  lowest_version?: number;
  name?: string;
  highest_version?: number;
}

const createOrUpdateStream = (
  kind: MediaType,
  consumer: types.Consumer,
  user: User
) => {
  let stream: MediaStream;
  if (kind === MediaType.AUDIO) {
    stream = createOrUpdateStreamAndAddTrack(
      consumer,
      user.transports.audio.stream
    );
  } else if (kind === MediaType.SCREEN) {
    stream = createOrUpdateStreamAndAddTrack(
      consumer,
      user.transports.screen.stream
    );
  } else {
    stream = createOrUpdateStreamAndAddTrack(
      consumer,
      user.transports.video.stream
    );
  }
  return stream;
};

const getTrack = (stream: MediaStream, kind: MediaType) => {
  let track: MediaStreamTrack;
  // This process is same for both video & screen share
  if (kind === MediaType.VIDEO) {
    track = stream.getVideoTracks()[0];
  } else if (kind === MediaType.SCREEN) {
    track = stream.getVideoTracks()[0];
  } else {
    track = stream.getAudioTracks()[0];
  }
  return track;
};

const isInRange = (value, comparator, errorMargin) => {
  const rangeLow = comparator - comparator * (errorMargin / 100);
  const rangeHigh = comparator + comparator * (errorMargin / 100);
  return rangeLow <= value && value <= rangeHigh;
};

export {
  getQueryParam,
  createUser,
  checkForExistingUser,
  addTransportToUser,
  addMediaTypeToUser,
  removeUser,
  applyDrag,
  generateItems,
  addStreamToUser,
  findUser,
  getCurrentTime,
  setSelfUserProperties,
  setUserType,
  getProducingUser,
  verifyCustomer,
  verifyGuest,
  setActualDimensions,
  getCompanyName,
  validateBrowsers,
  getAllQueryParams,
  createOrUpdateStream,
  getTrack,
  isSalesPerson,
  isShopKeeper,
  throttle,
  isInRange,
};
